import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NAVY, RADIUS_CARD, RADIUS_ITEM, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import type { FilterOption } from './types';

interface FilterDropdownProps {
  label?: string;
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
}

const BULK_BTN_SX = {
  textTransform: 'none' as const,
  fontFamily: 'Jost, sans-serif',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: `${NAVY}99`,
  minWidth: 'auto',
  px: 0.5,
  '&:hover': { color: NAVY },
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({ label, options, selectedValues, onChange }) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open && options.length > 1) {
      // Delay to let the Popover finish positioning before focusing
      const id = setTimeout(() => searchRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [open, options.length]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(lower) || (o.group?.toLowerCase().includes(lower) ?? false)
    );
  }, [options, search]);

  const toggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleSelectAll = useCallback(() => {
    const allFilteredValues = filtered.map((o) => o.value);
    const merged = new Set([...selectedValues, ...allFilteredValues]);
    onChange(Array.from(merged));
  }, [filtered, selectedValues, onChange]);

  const handleClearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const hasSelection = selectedValues.length > 0;

  // Build tooltip text from selected values mapped to labels
  const tooltipTitle = useMemo(() => {
    if (!hasSelection) return '';
    const labels = selectedValues.map((v) => options.find((o) => o.value === v)?.label ?? v).slice(0, 8);
    const remaining = selectedValues.length - labels.length;
    return labels.join('\n') + (remaining > 0 ? `\n+ ${remaining} más` : '');
  }, [selectedValues, options, hasSelection]);

  const handleOptionKeyDown = (e: React.KeyboardEvent, value: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle(value);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (e.currentTarget as HTMLElement).nextElementSibling as HTMLElement | null;
      next?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (e.currentTarget as HTMLElement).previousElementSibling as HTMLElement | null;
      prev?.focus();
    }
  };

  return (
    <>
      <Tooltip
        title={tooltipTitle}
        enterDelay={300}
        placement="bottom"
        slotProps={{
          tooltip: {
            sx: {
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.8rem',
              backgroundColor: NAVY,
              borderRadius: RADIUS_ITEM,
              whiteSpace: 'pre-line',
              maxWidth: 260,
            },
          },
        }}
      >
        <Button
          ref={anchorRef}
          onClick={() => setOpen(true)}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            height: 40,
            borderRadius: RADIUS_ITEM,
            backgroundColor: hasSelection ? `${NAVY}0A` : open ? `${NAVY}06` : SURFACE_CONTAINER_LOWEST,
            color: NAVY,
            textTransform: 'none',
            fontFamily: 'Jost, sans-serif',
            fontWeight: 500,
            fontSize: '0.875rem',
            px: 2,
            whiteSpace: 'nowrap',
            border: open
              ? `1px solid rgba(21, 24, 58, 0.35)`
              : hasSelection
                ? `1px solid rgba(21, 24, 58, 0.25)`
                : `1px solid rgba(21, 24, 58, 0.12)`,
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              backgroundColor: hasSelection ? `${NAVY}14` : `${NAVY}08`,
              border: `1px solid rgba(21, 24, 58, 0.35)`,
            },
          }}
        >
          {hasSelection ? (
            <Box
              component="span"
              sx={{
                backgroundColor: NAVY,
                color: '#fff',
                borderRadius: RADIUS_ITEM,
                px: 0.75,
                height: 20,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 700,
              }}
            >
              {selectedValues.length} selec.
            </Box>
          ) : (
            <Typography
              component="span"
              sx={{ color: `${NAVY}60`, fontFamily: 'Jost, sans-serif', fontSize: '0.875rem', fontWeight: 400 }}
            >
              {label ? `${label}: Todos` : 'Todos'}
            </Typography>
          )}
        </Button>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => {
          setOpen(false);
          setSearch('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: RADIUS_CARD,
              backgroundColor: '#ffffff',
              boxShadow: '0px 16px 48px rgba(21, 24, 58, 0.14), 0px 4px 12px rgba(21, 24, 58, 0.08)',
              border: `1px solid rgba(21, 24, 58, 0.10)`,
              minWidth: anchorRef.current?.offsetWidth ?? 240,
              maxHeight: 360,
              overflow: 'hidden',
            },
          },
        }}
      >
        {/* Search input */}
        {options.length > 1 && (
          <Box sx={{ p: 1.5, pb: 0.5 }}>
            <TextField
              size="small"
              placeholder="Buscar…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputRef={searchRef}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: RADIUS_ITEM,
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
        )}

        {/* Bulk actions */}
        {options.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, pt: 1, pb: 0.5 }}>
            <Button size="small" onClick={handleSelectAll} sx={BULK_BTN_SX}>
              Seleccionar todo
            </Button>
            <Button size="small" onClick={handleClearAll} disabled={!hasSelection} sx={BULK_BTN_SX}>
              Limpiar
            </Button>
          </Box>
        )}

        {/* Option list */}
        <Box role="listbox" sx={{ maxHeight: 280, overflowY: 'auto', py: 0.5 }}>
          {filtered.length === 0 ? (
            <Typography
              sx={{
                px: 2,
                py: 1.5,
                color: `${NAVY}80`,
                fontSize: '0.85rem',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              Sin resultados
            </Typography>
          ) : (
            (() => {
              const hasGroups = filtered.some((o) => o.group);
              let lastGroup: string | undefined;
              return filtered.map((option) => {
                const checked = selectedValues.includes(option.value);
                const showGroupHeader = hasGroups && option.group && option.group !== lastGroup;
                if (hasGroups) lastGroup = option.group;
                return (
                  <Box key={option.value}>
                    {showGroupHeader && (
                      <Typography
                        sx={{
                          px: 1.5,
                          pt: 1,
                          pb: 0.25,
                          fontSize: '0.7rem',
                          fontFamily: 'Jost, sans-serif',
                          fontWeight: 700,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          color: `${NAVY}80`,
                          backgroundColor: `${NAVY}04`,
                        }}
                      >
                        {option.group}
                      </Typography>
                    )}
                    <Box
                      role="option"
                      aria-selected={checked}
                      tabIndex={0}
                      onClick={() => toggle(option.value)}
                      onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: hasGroups ? 2.5 : 1.5,
                        pr: 1.5,
                        py: 0.5,
                        cursor: 'pointer',
                        outline: 'none',
                        '&:hover, &:focus-visible': { backgroundColor: `${NAVY}08` },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        tabIndex={-1}
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        sx={{ color: NAVY, '&.Mui-checked': { color: NAVY } }}
                      />
                      {option.color && (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: option.color,
                            flexShrink: 0,
                            mr: 0.75,
                          }}
                        />
                      )}
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontFamily: 'Jost, sans-serif',
                            color: option.color ?? NAVY,
                            fontWeight: checked ? 600 : 400,
                            lineHeight: 1.3,
                          }}
                        >
                          {option.label}
                        </Typography>
                        {option.description && (
                          <Typography
                            sx={{
                              fontSize: '0.72rem',
                              fontFamily: 'Jost, sans-serif',
                              color: `${NAVY}60`,
                              lineHeight: 1.3,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              });
            })()
          )}
        </Box>
      </Popover>
    </>
  );
};

export default FilterDropdown;
